import { useEffect, useState, useMemo } from "react";

import getLogger, { LogGroup } from "js/core/logger";
import { IWorkspaceMetadata, WorkspaceUserRole, WorkspacePlanTier } from "common/interfaces";

import { useUser } from "./useUser";

const logger = getLogger(LogGroup.WORKSPACES);

interface UseWorkspaceReturn {
    workspace: IWorkspaceMetadata | null;
    loading: boolean;
    error: Error | null;
}

interface UseUserWorkspacesReturn {
    workspaces: IWorkspaceMetadata[];
    loading: boolean;
    error: Error | null;
}

/**
 * Hook that loads all workspaces for the current user.
 * This is the base hook that fetches workspace data.
 */
export const useUserWorkspaces = (): UseUserWorkspacesReturn => {
    const { user, loading: userLoading, error: userError } = useUser();

    const [workspaces, setWorkspaces] = useState<IWorkspaceMetadata[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    useEffect(() => {
        if (!user || userLoading) {
            return;
        }

        if (userError) {
            setError(userError);
            setLoading(false);
            return;
        }

        const loadWorkspaces = async () => {
            try {
                // Ensure workspaces metadata is loaded
                if (!user.workspacesMetadata || user.workspacesMetadata.length === 0) {
                    await user.loadWorkspacesMetadata();
                }

                logger.info("[useUserWorkspaces] workspaces metadata loaded", {
                    count: user.workspacesMetadata.length,
                    userId: user.id
                });

                setWorkspaces(user.workspacesMetadata || []);
                setError(null);
            } catch (err) {
                logger.error(err, "Error loading workspaces");
                setError(err as Error);
            } finally {
                setLoading(false);
            }
        };

        loadWorkspaces();
    }, [user, userLoading, userError]);

    return { workspaces, loading, error };
};

/**
 * Hook that gets a specific workspace by ID.
 * Logs a warning if the workspace is not found.
 *
 * @param workspaceId - The ID of the workspace to retrieve
 * @returns The workspace, loading state, and any errors
 */
export const useWorkspace = (workspaceId: string): UseWorkspaceReturn => {
    const { workspaces, loading, error } = useUserWorkspaces();

    const workspace = useMemo(() => {
        if (loading || !workspaces.length || !workspaceId) {
            return null;
        }

        const found = workspaces.find(w => w.id === workspaceId);

        if (!found) {
            logger.warn("[useWorkspace] Workspace not found by id", {
                workspaceId,
                availableWorkspaces: workspaces.map(w => w.id)
            });
        }

        return found || null;
    }, [workspaces, workspaceId, loading]);

    return { workspace, loading, error };
};
